import React, { useMemo } from 'react';
import styles from './A23CashLobbyRoom.module.css';
// import GameTypeHeader from '@/src/components/GameTypeHeader/GameTypeHeader';
import { GameTypeHeader } from '@themed-components';
import { HEADER_GAME_TYPES, RUPEE_SYMBOL } from '@/Poker/data/Constants';
import { useParams } from 'react-router';
import useRoomDetails from '@/src/hooks/data/useRoomDetails';
import { FaUserAlt } from "react-icons/fa";
import gameAssets from '@themed-assets';
// import TableTile from '@/src/components/TableTile/TableTile';
import { TableTile } from '@themed-components';
import SortableTable from '@/src/components/SortableTable';
import useRoomTables from '@/src/hooks/utils/useRoomTables';
import ActiveGamesButton from '@/src/components/ActiveGamesButton/ActiveGamesButton';
import QuickJoinButton from '@/src/components/QuickJoinButton/QuickJoinButton';
// import LobbyBuyIn from '@/src/components/BuyIn/LobbyBuyIn';
import { LobbyBuyIn } from '@themed-components';
import TileSkeleton from '@/src/components/TileSkeleton/TileSkeleton';
import useLobbyConnectionStatus from '@/src/hooks/data/useLobbyConnectionStatus';

const FILTERS = [
  { label: "🔥 Turbo", icon: null },
  { label: "RIT", icon: gameAssets.rit },
  { label: "⚡ Straddle", icon: null },
  // { label: "Deep", icon: null },
  { label: "Anonymous", icon: gameAssets.anonymous },
  // { label: "Happy Hours", icon: null },
  { label: 'Hide Full Tables', icon: null },
  { label: 'Hide Empty Tables', icon: null },
];

const TABLE_HEADERS = [
  {
    key: 'tableData.averageChipsStack',
    label: 'AVG. STACK',
    align: 'alignLeft'
  },
  {
    key: 'tableData.seatedPlayerCount',
    label: 'PLAYERS',
    align: 'alignCenter'
  },
  {
    key: 'tableData.minimumBuyIn', // This will be used for buy-in display
    label: 'BUY IN',
    align: 'alignRight'
  }
];

export default function A23CashLobbyRoom() {
  const { roomId } = useParams();
  const { bigBlind, smallBlind, tableName, tables, onlineCount, minBuyInAmount, isTableListingEnabled, isFavorite } = useRoomDetails(roomId);
  const { connected } = useLobbyConnectionStatus();
  // Use the new useRoomTables hook
  const {
    tableDataArray,
    filteredTableData,
    appliedFilters,
    toggleFilter,
    removeFilter,
    clearAllFilters,
    hasActiveFilters,
    isLoading
  } = useRoomTables(tables, roomId);

  // Render function for each table row with error handling
  const renderTableRow = (item) => {
    try {
      if (!item || !item.tableId || !item.tableData) {
        console.warn('Invalid table item for rendering:', item);
        return null;
      }

      const { tableId, tableData } = item;
      return (
        <TableTile
          key={tableId}
          tableData={tableData}
          isTableListingEnabled={isTableListingEnabled}
        />
      );
    } catch (error) {
      console.error('Error rendering table row:', error);
      return null;
    }
  };

  // Render filter button with X mark for applied filters and error handling
  const renderFilterButton = (filter) => {
    try {
      if (!filter || !filter.label) {
        console.warn('Invalid filter for rendering:', filter);
        return null;
      }

      const isApplied = appliedFilters.has(filter.label);

      return (
        <div
          key={filter.label}
          className={
            styles.filterButton +
            (isApplied ? ' ' + styles.activeFilter : '')
          }
          onClick={() => toggleFilter(filter.label)}
        >
          {filter.icon && <img className={styles.filterIcon} src={filter.icon} alt={filter.label + ' icon'} />}
          <span>{filter.label}</span>
        </div>
      );
    } catch (error) {
      console.error('Error rendering filter button:', error);
      return null;
    }
  };

  // Separate applied and available filters for better UX with error handling
  const appliedFilterButtons = useMemo(() => {
    try {
      return FILTERS.filter(filter => filter && appliedFilters.has(filter.label));
    } catch (error) {
      console.error('Error processing applied filters:', error);
      return [];
    }
  }, [appliedFilters]);

  const availableFilterButtons = useMemo(() => {
    try {
      return FILTERS.filter(filter => filter && !appliedFilters.has(filter.label));
    } catch (error) {
      console.error('Error processing available filters:', error);
      return [];
    }
  }, [appliedFilters]);

  // Provide fallback values for missing data
  const safeBigBlind = bigBlind || 0;
  const safeSmallBlind = smallBlind || 0;
  const safeTableName = tableName || 'Unknown Table';
  const safeOnlineCount = onlineCount || 0;
  const safeMinBuyInAmount = minBuyInAmount || 0;

  return (
    <div className={styles.container}>
      {/* Header */}
      <GameTypeHeader headerName={safeTableName} type={HEADER_GAME_TYPES.TABLE} showGameTypes={false} isFavorite={isFavorite} roomId={roomId} />

      <div className={styles.offsetContainer}>
        {/* Row of dummy data (like recommended row) */}
        <div className={styles.tableDetailSection}>
          <div className={styles.table}>
            <div className={styles.tableDetailsRow}>
              <div className={`${styles.tableDetailsRowLabel} ${styles.alignLeft}`}>BLINDS</div>
              <div className={`${styles.tableDetailsRowLabel} ${styles.alignCenter}`}>PLAYING</div>
              <div className={`${styles.tableDetailsRowLabel} ${styles.alignCenter}`}>TABLES</div>
              <div className={`${styles.tableDetailsRowLabel} ${styles.alignRight}`}>MIN BUY IN</div>
            </div>
            <div className={styles.tableDetailsRow}>
              <div className={`${styles.tableDetailsRowValue} ${styles.alignLeft}`}>{safeSmallBlind}/{safeBigBlind}</div>
              <div className={`${styles.tableDetailsRowValue} ${styles.alignCenter}`}> <FaUserAlt color={'var(--color-secondary)'} size={12} /> <span>{safeOnlineCount}</span></div>
              <div className={`${styles.tableDetailsRowValue} ${styles.alignCenter}`}><div className={styles.greenDot}></div> {tableDataArray?.length || 0}</div>
              <div className={`${styles.tableDetailsRowValue} ${styles.alignRight}`}>
                {/* <img src={gameAssets.cashBill} />  */}
                <span className={styles.minBuyInAmount}>{RUPEE_SYMBOL + safeMinBuyInAmount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          {/* Applied filters first */}
          {appliedFilterButtons.map(renderFilterButton)}

          {/* Available filters */}
          {availableFilterButtons.map(renderFilterButton)}
        </div>



        <div className={styles.activeGamesAndQuickJoinContainer}>
          <ActiveGamesButton absolute={false} />
          {
            !isLoading && connected && (
              <QuickJoinButton minBuyInAmount={minBuyInAmount} gameDefId={filteredTableData[0]?.tableData?.gameDefId} />
            )
          }
        </div>

        {/* Sortable Table */}
        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingContent}>
              {
                Array.from({ length: 3 }).map((_, index) => (
                  <TileSkeleton key={index} />
                ))
              }
            </div>
          </div>
        ) : filteredTableData.length > 0 ? (
          <SortableTable
            headers={TABLE_HEADERS}
            data={filteredTableData}
            renderRow={renderTableRow}
            className={styles.sortableTable}
          />
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateContent}>
              <h3 className={styles.emptyStateTitle}>
                Oops! You cannot go all-in
              </h3>
              <p className={styles.emptyStateMessage}>
                There are no cash games that match your suit.
              </p>
              {hasActiveFilters && (
                <>
                  <p className={styles.emptyStateSubMessage}>
                    Remove a few filters for better results.
                  </p>
                  <button
                    className={styles.clearFilterButton}
                    onClick={clearAllFilters}
                  >
                    Clear Filters
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <LobbyBuyIn />
    </div>
  );
}
